import { VIRTUAL_WEBCAM_LABELS } from '../constants';

export type WebcamDetectionSource =
  | 'track_label'
  | 'track_settings'
  | 'capabilities'
  | 'enumerate_devices'
  | 'unknown';

export interface ActiveVideoDeviceInspection {
  activeDeviceLabel?: string;
  activeDeviceId?: string;
  activeDeviceIsVirtual?: boolean;
  installedVirtualDevices: string[];
  detectionSource: WebcamDetectionSource;
  capabilityReason?: string;
}

function normalizeLabel(label?: string | null) {
  return label?.trim().toLowerCase() ?? '';
}

function isVirtualDeviceLabel(label?: string | null) {
  const normalizedLabel = normalizeLabel(label);
  if (!normalizedLabel) return false;

  return VIRTUAL_WEBCAM_LABELS.some(virtualLabel =>
    normalizedLabel.includes(virtualLabel),
  );
}

function getInstalledVirtualDevices(devices: MediaDeviceInfo[]) {
  return devices
    .filter(
      device =>
        device.kind === 'videoinput' && isVirtualDeviceLabel(device.label),
    )
    .map(device => device.label);
}

function findDeviceById(devices: MediaDeviceInfo[], deviceId?: string) {
  if (!deviceId) return undefined;

  return devices.find(
    device => device.kind === 'videoinput' && device.deviceId === deviceId,
  );
}

export async function analyzeTrackCapabilities(
  track: MediaStreamTrack,
): Promise<{ isSynthetic: boolean; reason?: string }> {
  if (!track.getCapabilities) {
    return { isSynthetic: false };
  }

  const caps = track.getCapabilities();

  if (caps.width && caps.height) {
    const widthFixed =
      caps.width.min !== undefined &&
      caps.width.max !== undefined &&
      caps.width.min === caps.width.max;
    const heightFixed =
      caps.height.min !== undefined &&
      caps.height.max !== undefined &&
      caps.height.min === caps.height.max;

    if (widthFixed && heightFixed) {
      return {
        isSynthetic: true,
        reason: 'Resolucao fixa sem range',
      };
    }
  }

  if (caps.frameRate) {
    const fps = caps.frameRate.max;
    if (fps && fps !== 30 && fps !== 60 && fps !== 24 && fps !== 15) {
      return {
        isSynthetic: true,
        reason: `Frame rate fora do padrao: ${fps}fps`,
      };
    }
  }

  return { isSynthetic: false };
}

export async function inspectActiveVideoTrack(
  track: MediaStreamTrack,
  devices: MediaDeviceInfo[] = [],
): Promise<ActiveVideoDeviceInspection> {
  const settings = track.getSettings?.();
  const settingsDeviceId = settings?.deviceId;
  const trackLabel = track.label?.trim();
  const matchedDevice = findDeviceById(devices, settingsDeviceId);
  const resolvedLabel = trackLabel || matchedDevice?.label;
  const installedVirtualDevices = getInstalledVirtualDevices(devices);

  if (trackLabel && isVirtualDeviceLabel(trackLabel)) {
    return {
      activeDeviceLabel: resolvedLabel,
      activeDeviceId: settingsDeviceId,
      activeDeviceIsVirtual: true,
      installedVirtualDevices,
      detectionSource: 'track_label',
    };
  }

  if (matchedDevice && isVirtualDeviceLabel(matchedDevice.label)) {
    return {
      activeDeviceLabel: matchedDevice.label,
      activeDeviceId: matchedDevice.deviceId,
      activeDeviceIsVirtual: true,
      installedVirtualDevices,
      detectionSource: 'track_settings',
    };
  }

  if (trackLabel) {
    return {
      activeDeviceLabel: resolvedLabel,
      activeDeviceId: settingsDeviceId,
      activeDeviceIsVirtual: false,
      installedVirtualDevices,
      detectionSource: 'track_label',
    };
  }

  if (matchedDevice) {
    return {
      activeDeviceLabel: matchedDevice.label,
      activeDeviceId: matchedDevice.deviceId,
      activeDeviceIsVirtual: false,
      installedVirtualDevices,
      detectionSource: 'track_settings',
    };
  }

  const capabilities = await analyzeTrackCapabilities(track);
  if (capabilities.isSynthetic) {
    return {
      activeDeviceLabel: resolvedLabel,
      activeDeviceId: settingsDeviceId,
      activeDeviceIsVirtual: undefined,
      installedVirtualDevices,
      detectionSource: 'capabilities',
      capabilityReason: capabilities.reason,
    };
  }

  if (devices.length > 0) {
    return {
      activeDeviceLabel: resolvedLabel,
      activeDeviceId: settingsDeviceId,
      activeDeviceIsVirtual: undefined,
      installedVirtualDevices,
      detectionSource: 'enumerate_devices',
    };
  }

  return {
    activeDeviceLabel: resolvedLabel,
    activeDeviceId: settingsDeviceId,
    activeDeviceIsVirtual: undefined,
    installedVirtualDevices,
    detectionSource: 'unknown',
  };
}
